import React, { useContext, useEffect, useRef, useState } from 'react';
import { KeyRound, LogOut, MapPin } from 'lucide-react';
import { AuthContext } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useFeature } from '../../contexts/FeaturesContext';
import { authAPI, portalAPI } from '../../services/api';
import { confirmDialog, notify, reportLoadError } from '../../utils/notify';
import LanguageSwitcher from '../../components/LanguageSwitcher';

// The guard's own details (to correct, they ask HR), the language, a new
// password, the location consent (with gps_checkin on) and signing out.

const EMPTY_PASSWORD = { current_password: '', new_password: '', confirm_password: '' };

const PortalMe = () => {
  const { logout } = useContext(AuthContext);
  const { t, locale } = useLanguage();
  const gpsOn = useFeature('gps_checkin');
  const [me, setMe] = useState(null);
  const [consent, setConsent] = useState(null);
  const [pw, setPw] = useState(null);
  const [busy, setBusy] = useState(false);
  const tRef = useRef(t);
  tRef.current = t;

  useEffect(() => {
    let cancelled = false;
    portalAPI.me()
      .then((res) => { if (!cancelled) setMe(res.data.employee); })
      .catch((error) => reportLoadError(error, tRef.current('Error loading your details'), { main: true }));
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!gpsOn) return undefined;
    portalAPI.consent()
      .then((res) => { if (!cancelled) setConsent(res.data); })
      .catch((error) => reportLoadError(error, tRef.current('Error loading the location consent')));
    return () => { cancelled = true; };
  }, [gpsOn]);

  const changePassword = async (e) => {
    e.preventDefault();
    if (pw.new_password !== pw.confirm_password) {
      notify(t('The new passwords do not match'), 'error');
      return;
    }
    try {
      setBusy(true);
      await authAPI.changePassword({ current_password: pw.current_password, new_password: pw.new_password });
      notify(t('Password changed'), 'success');
      setPw(null);
    } catch (error) {
      notify(error.response?.data?.message ? t(error.response.data.message) : t('Error changing the password'), 'error');
    } finally {
      setBusy(false);
    }
  };

  const withdraw = async () => {
    if (!await confirmDialog(t('Withdraw your location consent? You can still check in, without a location.'))) return;
    try {
      await portalAPI.withdrawConsent();
      setConsent({ ...consent, accepted: false, accepted_at: null });
      notify(t('Location consent withdrawn'), 'success');
    } catch (error) {
      notify(t('Error saving your answer'), 'error');
    }
  };

  const row = (label, value) => value && (
    <div className="pt-def"><dt>{t(label)}</dt><dd>{value}</dd></div>
  );

  return (
    <div className="pt-page">
      <h1 className="pt-title">{me ? `${me.first_name} ${me.last_name}` : t('Me')}</h1>
      {me && (
        <section className="pt-card">
          <dl className="pt-defs">
            {row('Employee code', me.employee_code)}
            {row('Position', me.position)}
            {row('Phone', me.phone)}
            {row('Email', me.email)}
            {row('Address', [me.address, me.city].filter(Boolean).join(', '))}
            {row('Since', me.hire_date && new Date(`${me.hire_date}T12:00:00`).toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' }))}
          </dl>
          <p className="pt-muted">{t('Something wrong? Ask HR to correct it.')}</p>
        </section>
      )}

      <section className="pt-card">
        <h2 className="pt-card__title">{t('Language')}</h2>
        <LanguageSwitcher />
      </section>

      {gpsOn && consent && (
        <section className="pt-card">
          <h2 className="pt-card__title"><MapPin aria-hidden="true" /> {t('Location consent')}</h2>
          {consent.accepted ? (
            <>
              <p>{t('Accepted on {date} (version {version}).', { date: new Date(`${consent.accepted_at}:00`).toLocaleDateString(locale), version: consent.version })}</p>
              <button type="button" className="btn btn--secondary pt-big" onClick={withdraw}>{t('Withdraw consent')}</button>
            </>
          ) : (
            <p>{t('Not given. You will be asked at your next check-in.')}</p>
          )}
        </section>
      )}

      <section className="pt-card">
        <h2 className="pt-card__title"><KeyRound aria-hidden="true" /> {t('Password')}</h2>
        {!pw ? (
          <button type="button" className="btn btn--secondary pt-big" onClick={() => setPw({ ...EMPTY_PASSWORD })}>{t('Change password')}</button>
        ) : (
          <form className="pt-form" onSubmit={changePassword}>
            {[['current_password', 'Current password'], ['new_password', 'New password'], ['confirm_password', 'Repeat the new password']].map(([name, label]) => (
              <div key={name} className="pt-field">
                <label className="field-label" htmlFor={`pw-${name}`}>{t(label)}</label>
                <input id={`pw-${name}`} className="input" type="password" required minLength={name === 'current_password' ? undefined : 8}
                  autoComplete={name === 'current_password' ? 'current-password' : 'new-password'}
                  value={pw[name]} onChange={(e) => setPw({ ...pw, [name]: e.target.value })} />
              </div>
            ))}
            <div className="pt-actions">
              <button type="button" className="btn btn--secondary pt-big" onClick={() => setPw(null)}>{t('Cancel')}</button>
              <button type="submit" className="btn btn--primary pt-big" disabled={busy}>{t('Save')}</button>
            </div>
          </form>
        )}
      </section>

      <button type="button" className="btn btn--secondary pt-big pt-signout" onClick={logout}>
        <LogOut aria-hidden="true" /> {t('Sign out')}
      </button>
    </div>
  );
};

export default PortalMe;
